import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trackBuyClick } from "@/lib/track";
import { BenefitList } from "@/components/ProductCard";
import { StickyTopPick } from "@/components/home/StickyTopPick";
import { ProductGallery } from "@/components/ProductGallery";
import { PriceAlertForm } from "@/components/PriceAlertForm";
import { SEO } from "@/components/SEO";
import { CheckCircle, XCircle, Star, ArrowUpRight } from "@phosphor-icons/react";

export function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === false) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Product not found.</div>;
  if (!p) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Loading...</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.title,
    image: p.image_url,
    description: p.long_description || p.short_description,
    brand: { "@type": "Brand", name: (p.tags && p.tags[0]) || "HomeHearth" },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: p.rating,
      reviewCount: p.review_count || 1,
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: (p.price || "").replace(/[^\d.]/g, "") || "0",
      url: p.affiliate_url,
      availability: "https://schema.org/InStock",
    },
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 sm:py-12 pb-28 md:pb-12">
      <StickyTopPick product={p} label="Ready to buy?" showAfter={120} testid="product-sticky-bar" />
      <SEO
        title={p.title}
        description={p.short_description}
        image={p.image_url}
        path={`/product/${p.id}`}
        type="product"
        jsonLd={jsonLd}
      />
      <nav className="text-xs text-[#5C5A56] mb-5 sm:mb-8">
        <Link to="/" className="hover:text-[#5B7B53]">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/category/${encodeURIComponent(p.category)}`} className="hover:text-[#5B7B53]">{p.category}</Link>
      </nav>

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-10">
        <ProductGallery key={p.id} product={p} />

        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#5B7B53]">{p.category}</span>
          <h1 className="font-display text-3xl sm:text-5xl text-[#2C2B29] mt-2 sm:mt-3 leading-tight">{p.title}</h1>

          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={16} weight={i <= Math.round(p.rating) ? "fill" : "regular"} className={i <= Math.round(p.rating) ? "text-[#D17A58]" : "text-[#E5E0D8]"} />
            ))}
            <span className="text-sm text-[#5C5A56]">{p.rating} · {p.review_count.toLocaleString()} reviews</span>
          </div>

          <BenefitList items={p.benefits} testid="product-benefits" />
          <p className="text-base sm:text-lg text-[#5C5A56] mt-5 sm:mt-6 leading-relaxed">{p.long_description || p.short_description}</p>

          <div className="flex flex-wrap items-center gap-4 mt-6 sm:mt-8">
            <span className="text-3xl font-semibold text-[#2C2B29]">{p.price}</span>
            <a
              data-testid="product-buy-btn"
              href={p.affiliate_url}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              onClick={() => trackBuyClick(p.id)}
              className="hh-btn-primary inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3.5 text-sm font-semibold max-sm:w-full"
            >
              Buy Now on Amazon <ArrowUpRight size={16} weight="bold" />
            </a>
          </div>
          <div className="mt-6 max-w-lg"><PriceAlertForm key={p.id} products={[p]} testid="product-price-alert" /></div>

          {p.features?.length > 0 && (
            <div className="mt-10">
              <h3 className="font-display text-xl text-[#2C2B29] mb-3">Key features</h3>
              <ul className="space-y-1.5 text-sm text-[#2C2B29]">
                {p.features.map((f) => <li key={f}>• {f}</li>)}
              </ul>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {p.pros?.length > 0 && (
              <div className="rounded-2xl border border-[#E5E0D8] p-5 bg-white">
                <h3 className="font-display text-lg text-[#5B7B53] mb-3 flex items-center gap-2">
                  <CheckCircle size={20} weight="duotone" /> What we love
                </h3>
                <ul className="space-y-2 text-sm text-[#2C2B29]">
                  {p.pros.map((x) => <li key={x}>• {x}</li>)}
                </ul>
              </div>
            )}
            {p.cons?.length > 0 && (
              <div className="rounded-2xl border border-[#E5E0D8] p-5 bg-white">
                <h3 className="font-display text-lg text-[#D17A58] mb-3 flex items-center gap-2">
                  <XCircle size={20} weight="duotone" /> Watch-outs
                </h3>
                <ul className="space-y-2 text-sm text-[#5C5A56]">
                  {p.cons.map((x) => <li key={x}>• {x}</li>)}
                </ul>
              </div>
            )}
          </div>

          <p className="text-xs text-[#5C5A56] mt-8 leading-relaxed">
            As an Amazon Associate we earn from qualifying purchases. Prices shown are approximate — check Amazon for real-time pricing and availability.
          </p>
        </div>
      </div>
    </div>
  );
}

export function DisclosurePage() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-lg">
      <SEO
        title="Affiliate disclosure"
        description="How HomeHearth earns via the Amazon Associates programme and our editorial independence policy."
        path="/disclosure"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Transparency</p>
      <h1 className="font-display text-5xl text-[#2C2B29] mb-8">Affiliate disclosure</h1>
      <div className="space-y-5 text-[#2C2B29] leading-relaxed">
        <p>
          HomeHearth is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.com.
        </p>
        <p>
          When you click a product link on this site and make a purchase, we may earn a small commission — <strong>at no additional cost to you</strong>. These commissions help us keep the lights on and continue publishing independent reviews.
        </p>
        <p>
          <strong>What we don't do:</strong> we don't accept paid placements, sponsored posts disguised as reviews, or affiliate commissions from brands to change our editorial rankings. Every product on this site was chosen because we genuinely think it belongs on the list.
        </p>
        <p>
          Prices, availability, and product specifications are subject to change. We do our best to keep our recommendations current, but we recommend confirming details on Amazon before you buy.
        </p>
        <p>
          Questions? Reach us anytime at <a className="hh-link" href="mailto:hello@homehearth.example">hello@homehearth.example</a>.
        </p>
      </div>
    </div>
  );
}
